import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { courseLevelsApi } from "@/api";
import type { CreateCourseLevelData, UpdateCourseLevelData } from "@/api";
import { QUERY_KEYS } from "@/constants";

export const useCourseLevels = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_LEVELS],
    queryFn: () => courseLevelsApi.getAll(),
    select: res => res.data,
  });
};

export const useCreateCourseLevel = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCourseLevelData) => courseLevelsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_LEVELS] });
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdateCourseLevel = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCourseLevelData }) =>
      courseLevelsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_LEVELS] });
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeleteCourseLevel = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => courseLevelsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_LEVELS] });
      toast.success(t("COMMON.DELETED"));
    },
  });
};
